import React, { useState, useEffect } from 'react';
import { InventoryBalanceItem, AppTab, AuthUser } from '../types';
import { api } from '../api';
import { tg } from '../telegram';
import { Package, Truck, CheckCircle2, AlertTriangle, Printer, Search, RefreshCw, ArrowRight, Trash2, Scale, Lock } from 'lucide-react';
import { formatNumber, formatQty } from '../utils/format';
import toast from 'react-hot-toast';

interface Props {
  onNavigate: (tab: AppTab) => void;
  onOpenPrintModal: () => void;
  currentUser?: AuthUser | null;
}

export const DashboardScreen: React.FC<Props> = ({ onNavigate, onOpenPrintModal, currentUser }) => {
  const [balances, setBalances] = useState<InventoryBalanceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [warehouseFilter, setWarehouseFilter] = useState<'STORE' | 'CENTRAL'>('STORE');
  const [searchTerm, setSearchTerm] = useState('');
  const [onlyLowStock, setOnlyLowStock] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await api.getInventory();
      setBalances(data);
    } catch (err: any) {
      tg.alert(`Không tải được dữ liệu tồn kho: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const storeItems = balances.filter((b) => b.warehouse === 'STORE');
  // Kho Tổng tuyệt đối không có BTP (Bán thành phẩm)
  const centralItems = balances.filter((b) => b.warehouse === 'CENTRAL' && b.type !== 'PREPARED');

  const lowStockStoreItems = storeItems.filter(
    (b) => b.safety_stock && b.safety_stock > 0 && b.quantity <= b.safety_stock
  );
  const lowStockCentralItems = centralItems.filter(
    (b) => b.safety_stock && b.safety_stock > 0 && b.quantity <= b.safety_stock
  );

  const currentWarehouseItems = warehouseFilter === 'STORE' ? storeItems : centralItems;

  const displayedItems = currentWarehouseItems.filter((b) => {
    const matchesSearch = b.ingredient_name.toLowerCase().includes(searchTerm.toLowerCase().trim());
    if (!matchesSearch) return false;
    if (onlyLowStock) {
      return Boolean(b.safety_stock && b.safety_stock > 0 && b.quantity <= b.safety_stock);
    }
    return true;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* 5 Core Operations Action Grid */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          5 Nghiệp Vụ Kho Vận Hành
        </div>

        {/* Primary 3 Flows */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          <button
            className="card"
            onClick={() => {
              if (currentUser?.role === 'STAFF') {
                tg.haptic.warning();
                toast.error('Nghiệp vụ L1 (Nhập kho tổng từ NCC) chỉ dành cho Quản lý / Admin');
                return;
              }
              tg.haptic.impact('medium');
              onNavigate('INWARD');
            }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 8,
              padding: '12px 6px',
              cursor: 'pointer',
              border: '1px solid var(--border-color)',
              opacity: currentUser?.role === 'STAFF' ? 0.65 : 1,
              position: 'relative',
            }}
          >
            {currentUser?.role === 'STAFF' && (
              <span
                style={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  fontSize: 9,
                  background: 'var(--bg-surface-subtle)',
                  padding: '2px 4px',
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  color: 'var(--text-tertiary)',
                  fontWeight: 700,
                }}
              >
                <Lock size={9} /> Admin
              </span>
            )}
            <div style={{ width: 38, height: 38, borderRadius: '50%', backgroundColor: '#EAF6EE', color: '#1E7B48', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Package size={18} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 800 }}>L1: Nhập Kho</div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>Kho Tổng</div>
            </div>
          </button>

          <button
            className="card"
            onClick={() => { tg.haptic.impact('medium'); onNavigate('TRANSFER'); }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 8,
              padding: '12px 6px',
              cursor: 'pointer',
              border: '1px solid var(--border-color)',
            }}
          >
            <div style={{ width: 38, height: 38, borderRadius: '50%', backgroundColor: '#FEF3C7', color: '#D97706', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Truck size={18} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 800 }}>L2: Chuyển Kho</div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>Xuất Quầy</div>
            </div>
          </button>

          <button
            className="card"
            onClick={() => { tg.haptic.impact('medium'); onNavigate('RECONCILE'); }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 8,
              padding: '12px 6px',
              cursor: 'pointer',
              border: '1px solid var(--border-color)',
            }}
          >
            <div style={{ width: 38, height: 38, borderRadius: '50%', backgroundColor: '#FEE2E2', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle2 size={18} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 800 }}>L3: Chốt Ca</div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>Chốt tồn & Tiêu thụ NL</div>
            </div>
          </button>
        </div>

        {/* Secondary 2 Flows: Waste & Periodic Balance */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <button
            className="card"
            onClick={() => { tg.haptic.impact('medium'); onNavigate('WASTE'); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              cursor: 'pointer',
              border: '1px solid var(--border-color)',
            }}
          >
            <div style={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: '#FEE2E2', color: '#EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Trash2 size={18} />
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 13, fontWeight: 800 }}>L4: Báo Hủy / Hỏng</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Hủy NL thô & BTP nấu hỏng</div>
            </div>
          </button>

          <button
            className="card"
            onClick={() => { tg.haptic.impact('medium'); onNavigate('BALANCE'); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              cursor: 'pointer',
              border: '1px solid var(--border-color)',
            }}
          >
            <div style={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: '#E0E7FF', color: '#4F46E5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Scale size={18} />
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 13, fontWeight: 800 }}>L5: Cân Bằng Kho</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Kho Tổng & Quầy Bar</div>
            </div>
          </button>
        </div>
      </div>

      {/* Quick Print Template Action */}
      <button
        onClick={() => { tg.haptic.impact('light'); onOpenPrintModal(); }}
        className="btn btn-secondary btn-full"
        style={{ justifyContent: 'space-between', padding: '10px 14px', minHeight: 44 }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700 }}>
          <Printer size={16} color="var(--primary-accent)" />
          In mẫu giấy kiểm kê (A4 hoặc Máy in nhiệt K80)
        </span>
        <ArrowRight size={14} color="var(--text-tertiary)" />
      </button>

      {/* Instant Inventory Balance Table */}
      <div className="card">
        <div className="card-header" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div className="card-title">
            <span>Tồn Kho Tức Thời</span>
            <span className="badge badge-neutral num">{displayedItems.length}</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Warehouse Toggle Tab */}
            <div style={{ display: 'inline-flex', background: 'var(--bg-surface-subtle)', borderRadius: 'var(--radius-sm)', padding: 2, gap: 2 }}>
              <button
                type="button"
                onClick={() => { tg.haptic.selectionChanged(); setWarehouseFilter('STORE'); }}
                style={{
                  border: 'none',
                  padding: '4px 10px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: warehouseFilter === 'STORE' ? 'var(--primary-accent)' : 'transparent',
                  color: warehouseFilter === 'STORE' ? '#fff' : 'var(--text-secondary)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <span>Cửa Hàng</span>
                {lowStockStoreItems.length > 0 && (
                  <span
                    style={{
                      background: warehouseFilter === 'STORE' ? '#EF4444' : '#FCA5A5',
                      color: '#fff',
                      borderRadius: 99,
                      padding: '0 5px',
                      fontSize: 10,
                      fontWeight: 800,
                      lineHeight: '14px',
                    }}
                  >
                    {lowStockStoreItems.length}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => { tg.haptic.selectionChanged(); setWarehouseFilter('CENTRAL'); }}
                style={{
                  border: 'none',
                  padding: '4px 10px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: warehouseFilter === 'CENTRAL' ? 'var(--primary-accent)' : 'transparent',
                  color: warehouseFilter === 'CENTRAL' ? '#fff' : 'var(--text-secondary)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <span>Kho Tổng</span>
                {lowStockCentralItems.length > 0 && (
                  <span
                    style={{
                      background: warehouseFilter === 'CENTRAL' ? '#EA580C' : '#FDBA74',
                      color: '#fff',
                      borderRadius: 99,
                      padding: '0 5px',
                      fontSize: 10,
                      fontWeight: 800,
                      lineHeight: '14px',
                    }}
                  >
                    {lowStockCentralItems.length}
                  </span>
                )}
              </button>
            </div>

            {/* Refresh Button */}
            <button
              type="button"
              onClick={() => { tg.haptic.impact('light'); loadData(); }}
              className="stepper-btn"
              style={{
                color: 'var(--primary-accent)',
                background: 'var(--primary-accent-subtle)',
                width: 30,
                height: 30,
                padding: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 'var(--radius-sm)',
                border: 'none',
              }}
              title="Làm mới tồn kho"
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Low Stock Warning Banner - STORE */}
        {warehouseFilter === 'STORE' && lowStockStoreItems.length > 0 && (
          <div
            style={{
              marginBottom: 12,
              padding: '8px 12px',
              backgroundColor: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <AlertTriangle size={16} color="#DC2626" style={{ flexShrink: 0 }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#991B1B' }}>
              Có <b>{lowStockStoreItems.length}</b> nguyên liệu tại Quầy sắp hết (cần châm thêm)!
            </span>
          </div>
        )}

        {/* Low Stock Warning Banner - CENTRAL */}
        {warehouseFilter === 'CENTRAL' && lowStockCentralItems.length > 0 && (
          <div
            style={{
              marginBottom: 12,
              padding: '8px 12px',
              backgroundColor: '#FFF7ED',
              border: '1px solid #FFEDD5',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <AlertTriangle size={16} color="#EA580C" style={{ flexShrink: 0 }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#C2410C' }}>
              Có <b>{lowStockCentralItems.length}</b> nguyên liệu tại Kho Tổng sắp hết (cần nhập hàng NCC)!
            </span>
          </div>
        )}

        {/* Warehouse Indicator Status */}
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>Đang hiển thị:</span>
          <span
            className="badge"
            style={{
              backgroundColor: warehouseFilter === 'CENTRAL' ? '#FFF7ED' : '#EFF6FF',
              color: warehouseFilter === 'CENTRAL' ? '#C2410C' : '#1D4ED8',
              border: warehouseFilter === 'CENTRAL' ? '1px solid #FFEDD5' : '1px solid #DBEAFE',
              fontWeight: 700,
              fontSize: 11,
              padding: '3px 8px',
            }}
          >
            {warehouseFilter === 'CENTRAL'
              ? '🏢 Kho Tổng (Chỉ lưu trữ Nguyên liệu thô)'
              : '☕ Quầy Bar Cửa Hàng (Gồm NL thô & BTP tại quầy)'}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          <div style={{ position: 'relative' }}>
            <Search size={16} color="var(--text-tertiary)" style={{ position: 'absolute', left: 12, top: 14 }} />
            <input
              type="text"
              className="input"
              placeholder={warehouseFilter === 'CENTRAL' ? "Tìm tên nguyên liệu thô (Kho Tổng)..." : "Tìm tên nguyên liệu / bán thành phẩm..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: 36 }}
            />
          </div>

          {/* Quick Check Filter for Low Stock Items */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 4px' }}>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                userSelect: 'none',
                fontSize: 12.5,
                fontWeight: 700,
                color: onlyLowStock ? '#DC2626' : 'var(--text-secondary)',
              }}
            >
              <input
                type="checkbox"
                checked={onlyLowStock}
                onChange={(e) => {
                  tg.haptic.selectionChanged();
                  setOnlyLowStock(e.target.checked);
                }}
                style={{
                  width: 17,
                  height: 17,
                  accentColor: '#DC2626',
                  cursor: 'pointer',
                }}
              />
              <span>⚠️ Chỉ hiển thị các món dưới ngưỡng an toàn</span>
            </label>

            <span
              className="badge"
              style={{
                backgroundColor: onlyLowStock ? '#FEE2E2' : 'var(--bg-surface-subtle)',
                color: onlyLowStock ? '#DC2626' : 'var(--text-tertiary)',
                fontWeight: 800,
                fontSize: 11,
              }}
            >
              {displayedItems.length} món
            </span>
          </div>
        </div>

        {/* List items */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-tertiary)' }}>
            <RefreshCw size={20} className="animate-spin" style={{ margin: '0 auto 8px' }} />
            Đang tải dữ liệu kho...
          </div>
        ) : displayedItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-tertiary)', fontSize: 13 }}>
            Không tìm thấy nguyên liệu nào phù hợp.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {displayedItems.map((item, idx) => {
              const isLow = Boolean(
                item.safety_stock &&
                item.safety_stock > 0 &&
                item.quantity <= item.safety_stock
              );

              return (
                <div
                  key={`${item.warehouse}_${item.ingredient_name}_${idx}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 12px',
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: isLow ? 'var(--color-danger-bg)' : 'var(--bg-surface-subtle)',
                    border: isLow ? '1px solid var(--color-danger)' : '1px solid transparent',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
                      {item.ingredient_name}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                      <span className="badge badge-neutral" style={{ fontSize: 10 }}>
                        {item.type === 'PREPARED' ? 'Bán thành phẩm' : 'Nguyên liệu thô'}
                      </span>
                      {isLow && (
                        <span className="badge badge-danger" style={{ fontSize: 10 }}>
                          Tối thiểu: {formatQty(item.safety_stock, item.unit)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ textAlign: 'right' }}>
                    <span className="num" style={{ fontSize: 18, fontWeight: 800, color: isLow ? 'var(--color-danger)' : 'var(--text-primary)' }}>
                      {formatNumber(item.quantity)}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 4 }}>
                      {item.unit || ''}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
